import { useCallback, useEffect, useState } from 'react';
import { AutocompleteInput, ReferenceInput, useDataProvider } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { MAX_NUMBER_OF_ENTITIES } from '@app/constants';
import { genericfilterToQuery } from '@app/utils/filterUtils';
import { DEFAULT_LANGUAGE } from '@app/utils/languages';
import { Box } from '@mui/material';
import _debounce from 'lodash/debounce';

import { GenericObject, MultiLingualObj } from '@app/components/common/Table/DefaultTable/types';

const toChoices = (items: GenericObject[]) =>
  items.map((item) => ({ id: item.id, name: (item.category_name as MultiLingualObj)[DEFAULT_LANGUAGE] }));

const TopicsSelectInput = () => {
  const { setValue } = useFormContext();
  const dataProvider = useDataProvider();
  const categoryId = useWatch({ name: 'categoryId' });

  const [topics, setTopics] = useState([]);

  const fetchTopics = useCallback(() => {
    dataProvider
      .fetchCategoryTopics('categories', categoryId)
      .then(({ data }: any) => {
        setTopics(data);
      })
      .catch((error: any) => {
        console.error(error);
      });
  }, [dataProvider, categoryId]);

  useEffect(() => {
    if (!categoryId) {
      setValue('topicId', null);
      return;
    }

    categoryId && fetchTopics();
  }, [categoryId, fetchTopics, setValue]);

  return (
    <AutocompleteInput
      source="topicId"
      label="Topic"
      disabled={!categoryId || !topics.length}
      choices={toChoices(topics)}
      variant="outlined"
      helperText={false}
      defaultValue={0}
      emptyValue={0}
      fullWidth
    />
  );
};

interface MediaFilterProps {
  statusFilterDisabled?: boolean;
}

export const MediaFilter = (props: MediaFilterProps) => {
  return (
    <>
      <Box component="span" mr={2} sx={{ width: '100%' }}>
        <ReferenceInput source="categoryId" reference="categories" perPage={MAX_NUMBER_OF_ENTITIES}>
          <AutocompleteInput
            label="Category"
            variant="outlined"
            helperText={false}
            defaultValue={0}
            emptyValue={0} // Passing in 0 for companyId in the API removes the association
            fullWidth
            optionText={(record) => record.category_name[DEFAULT_LANGUAGE]}
            filterToQuery={genericfilterToQuery}
          />
        </ReferenceInput>
      </Box>
      <Box component="span" mr={2} sx={{ width: '100%' }}>
        <TopicsSelectInput />
      </Box>
      <Box component="span" mr={2} sx={{ width: '100%' }}>
        <ReferenceInput
          source="companyId"
          reference="companies"
          perPage={MAX_NUMBER_OF_ENTITIES}
          sort={{ field: 'short_name', order: 'ASC' }}>
          <AutocompleteInput
            label="Company"
            variant="outlined"
            helperText={false}
            defaultValue={null}
            emptyValue={0} // Passing in 0 for companyId in the API removes the association
            fullWidth
            optionText={(record) => record.name[DEFAULT_LANGUAGE]}
            filterToQuery={genericfilterToQuery}
          />
        </ReferenceInput>
      </Box>
      <Box component="span" mr={2} sx={{ width: '100%' }}>
        <AutocompleteInput
          defaultValue={null}
          emptyValue={''}
          variant="outlined"
          label="Status"
          emptyText={'Select'}
          helperText={false}
          fullWidth
          disabled={props.statusFilterDisabled}
          source="statusType"
          choices={[
            { id: 'ACTIVE', name: 'Active' },
            { id: 'INACTIVE', name: 'Inactive' },
          ]}
        />
      </Box>
    </>
  );
};
